import { KeyboardArrowDown } from '@mui/icons-material';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BulkActionButton } from '~/components/bulk-actions';
import type { DeviceBulkSelectionInput } from '~/generated/graphql';
import { useBulkActionIds } from '~/hooks/unique-id';
import { CommandPopover, type DeviceCommandHandlers } from './CommandPopover';

export type BulkActionsProps = DeviceCommandHandlers & {
  selected: DeviceBulkSelectionInput;
  // Used when selection is condition-based (no explicit ids), to render the
  // popover subject as "%{count} Devices selected".
  totalCount: number;
  onComplete?: () => void;
};

export const BulkActions = ({
  selected,
  totalCount,
  onComplete,
  ...handlers
}: BulkActionsProps) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { buttonId, menuId } = useBulkActionIds();

  const close = useCallback(() => setOpen(false), []);

  return (
    <>
      <BulkActionButton
        ref={buttonRef}
        disableRipple
        id={buttonId}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={() => setOpen(true)}
      >
        {t('common.buttons.bulk_actions')}
        <KeyboardArrowDown />
      </BulkActionButton>
      <CommandPopover
        {...handlers}
        anchorEl={buttonRef.current}
        labelledBy={buttonId}
        menuId={menuId}
        open={open}
        onClose={close}
        selected={selected}
        subject={t('pages.devices.bulk_actions.selection_subject_plural', {
          count: selected.ids?.length ?? totalCount,
        })}
        onComplete={onComplete}
      />
    </>
  );
};
